// src/components/ChatMarkdown.jsx
//
// Safe markdown renderer for AI Study Partner chat answers (and any less-trusted AI
// text). The AI emits markdown (**bold** / *italic* / lists / `code` / fenced blocks /
// blockquotes / tables), which the panel used to show as raw `**` markers. This renders
// the SAFE SUBSET with react-markdown -- the SAME library the /legal page uses -- with:
//   - RAW HTML DISABLED (no rehype-raw): prompt-injected HTML like <img onerror=...> is
//     shown as escaped TEXT, never parsed/executed. This is react-markdown's default.
//   - remark-gfm: tables / strikethrough / task lists (all safe, no HTML; grading
//     feedback may emit tables).
//   - remark-breaks: a single newline becomes a line break, matching the old
//     whitespace-pre-wrap feel (the AI answer arrives whole, using single \n between
//     lines) without merging intended line breaks.
//   - Headings CLAMPED to chat-bubble scale (a bubble must never render a giant h1).
//   - Links: http(s)/mailto only, target=_blank rel="noopener noreferrer"; anything else
//     (javascript:/data:/relative) renders as plain text. react-markdown's default
//     urlTransform already strips javascript:/data:, so this is belt-and-suspenders.
// NO dangerouslySetInnerHTML anywhere; react-markdown builds a React tree, not HTML.
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { stripDanielleReviewNotice } from '../../functions/src/ai/reviewNotice.js';

// Only these schemes may become a navigable link; everything else is de-linked.
const SAFE_LINK = /^(https?:|mailto:)/i;

const components = {
    p: ({ children }) => <p className="font-serif leading-relaxed mb-2 last:mb-0">{children}</p>,
    // Headings exist in the safe subset but are clamped small for a chat bubble.
    h1: ({ children }) => <p className="font-display text-book-accent text-base font-semibold mt-1 mb-1.5">{children}</p>,
    h2: ({ children }) => <p className="font-display text-book-accent text-sm font-semibold mt-1 mb-1.5">{children}</p>,
    h3: ({ children }) => <p className="font-display text-book-accent text-sm font-semibold mt-1 mb-1">{children}</p>,
    h4: ({ children }) => <p className="font-display text-book-accent text-sm font-semibold mt-1 mb-1">{children}</p>,
    h5: ({ children }) => <p className="font-display text-book-accent text-sm font-semibold mt-1 mb-1">{children}</p>,
    h6: ({ children }) => <p className="font-display text-book-accent text-sm font-semibold mt-1 mb-1">{children}</p>,
    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-0.5 font-serif leading-relaxed">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-0.5 font-serif leading-relaxed">{children}</ol>,
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-book-text">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    a: ({ href, children }) => (
        typeof href === 'string' && SAFE_LINK.test(href)
            ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-book-accent underline hover:text-book-accentLight break-words">{children}</a>
            : <span>{children}</span>
    ),
    blockquote: ({ children }) => <blockquote className="border-l-2 border-book-accent/50 pl-3 my-2 italic text-book-muted font-serif">{children}</blockquote>,
    // Fenced blocks carry a language- class -> keep flush (the <pre> wrapper styles the
    // box); inline code (no class) gets its own subtle chip.
    code: ({ className, children }) => (
        /language-/.test(className || '')
            ? <code className={`${className || ''} font-mono text-[0.85em]`}>{children}</code>
            : <code className="font-mono text-[0.85em] bg-black/25 text-book-accentLight px-1 py-0.5 rounded">{children}</code>
    ),
    pre: ({ children }) => <pre className="bg-book-bg border border-book-accent/20 rounded p-2.5 my-2 overflow-x-auto text-[0.85em] leading-snug whitespace-pre">{children}</pre>,
    table: ({ children }) => (
        <div className="overflow-x-auto my-2">
            <table className="w-full border-collapse text-[0.9em] font-serif">{children}</table>
        </div>
    ),
    thead: ({ children }) => <thead className="border-b border-book-accent/30">{children}</thead>,
    th: ({ children }) => <th className="text-left font-semibold px-2 py-1 border border-book-accent/15">{children}</th>,
    td: ({ children }) => <td className="px-2 py-1 border border-book-accent/15 align-top">{children}</td>,
    hr: () => <hr className="border-book-accent/20 my-2" />,
};

export default function ChatMarkdown({ children }) {
    return (
        <div className="font-serif text-book-text text-sm leading-relaxed break-words">
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={components}>
                {typeof children === 'string' ? stripDanielleReviewNotice(children) : ''}
            </ReactMarkdown>
        </div>
    );
}
